import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  BookOpen, CheckCircle2, XCircle, ChevronRight, 
  ChevronLeft, RotateCw, Target, HelpCircle, Lock, Crown, Sparkles
} from "lucide-react";

const ChapterPractice = ({ auth }) => {
  const [chapters, setChapters] = useState([]);
  const [chapterQuestionCounts, setChapterQuestionCounts] = useState({});
  const [chapterProgress, setChapterProgress] = useState({});
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showWrongOnly, setShowWrongOnly] = useState(false);

  const isFreeUser = !auth.user || auth.user.subscription_tier === "free";
  
  // Free users can access Chapter 1 (Applying for Citizenship) and Chapter 2 (Rights and Responsibilities)
  const FREE_CHAPTERS = ["ch1", "ch2", "chapter_1", "chapter_2"];
  const isChapterFree = (chapterId) => {
    if (!chapterId) return false;
    const normalized = chapterId.replace("chapter_", "ch");
    return FREE_CHAPTERS.includes(normalized) || FREE_CHAPTERS.includes(chapterId);
  };

  useEffect(() => {
    fetchChaptersAndCounts();
  }, []);

  const fetchChaptersAndCounts = async () => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      
      const [chaptersRes, questionsRes, progressRes] = await Promise.all([
        axios.get(`${API}/chapters`),
        axios.get(`${API}/questions?limit=500`),
        auth.token ? axios.get(`${API}/progress/chapters/all`, { headers }) : Promise.resolve({ data: {} })
      ]);
      
      setChapters(chaptersRes.data);
      
      const counts = {};
      questionsRes.data.forEach(q => {
        counts[q.chapter] = (counts[q.chapter] || 0) + 1;
      });
      setChapterQuestionCounts(counts);
      setChapterProgress(progressRes.data || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load chapters");
    } finally {
      setLoading(false);
    }
  };

  const startChapterPractice = async (chapterId, reviewWrongOnly = false) => {
    setLoading(true);
    setShowWrongOnly(reviewWrongOnly);
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      
      const [questionsRes, progressRes] = await Promise.all([
        axios.get(`${API}/questions?chapter=${chapterId}&limit=100`),
        auth.token ? axios.get(`${API}/progress/chapter/${chapterId}`, { headers }) : Promise.resolve({ data: {} })
      ]);
      
      let allQuestions = questionsRes.data;
      const progress = progressRes.data || {};
      
      // If reviewing wrong answers only, filter questions
      if (reviewWrongOnly && progress.wrong_answers?.length > 0) {
        allQuestions = allQuestions.filter(q => 
          progress.wrong_answers.includes(q.question_id)
        );
        if (allQuestions.length === 0) {
          toast.info("No wrong answers to review! Great job!");
          setLoading(false);
          return;
        }
      }
      
      setQuestions(allQuestions);
      setSelectedChapter(chapterId);
      
      // Restore previous answers from progress
      const restoredAnswers = {};
      if (progress.questions_answered) {
        allQuestions.forEach((q, idx) => {
          if (progress.questions_answered.includes(q.question_id)) {
            const isCorrect = progress.correct_answers?.includes(q.question_id);
            restoredAnswers[idx] = {
              selected: isCorrect ? q.correct_answer : "wrong", // We don't store exact wrong answer, so mark as answered
              correct: isCorrect
            };
          }
        });
      }
      
      setAnswers(restoredAnswers);
      
      // Find first unanswered question to resume from
      const firstUnanswered = allQuestions.findIndex((q, idx) => !restoredAnswers[idx]);
      setCurrentIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    if (showExplanation) return;
    
    const question = questions[currentIndex];
    const isCorrect = answer === question.correct_answer;
    
    setSelectedAnswer(answer);
    setShowExplanation(true);
    setAnswers(prev => ({
      ...prev,
      [currentIndex]: {
        selected: answer,
        correct: isCorrect
      }
    }));
    
    // Save progress to backend
    if (auth.token) {
      try {
        const headers = { Authorization: `Bearer ${auth.token}` };
        await axios.post(
          `${API}/progress/chapter/${selectedChapter}/answer`,
          null,
          { 
            headers,
            params: {
              question_id: question.question_id,
              selected_answer: answer,
              is_correct: isCorrect
            }
          }
        );
      } catch (error) {
        console.error("Failed to save progress:", error);
      }
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(answers[currentIndex + 1]?.selected || null);
      setShowExplanation(!!answers[currentIndex + 1]);
    }
  };

  const prevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswer(answers[currentIndex - 1]?.selected || null);
      setShowExplanation(!!answers[currentIndex - 1]);
    }
  };

  const resetPractice = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setAnswers({});
  };

  const backToChapters = () => {
    setSelectedChapter(null);
    setQuestions([]);
    setAnswers({});
  };

  const getChapterIcon = (chapterId) => {
    const icons = {
      ch1: "📋", ch2: "⚖️", ch3: "🍁", ch4: "📜",
      ch5: "🏛️", ch6: "🗳️", ch7: "✅", ch8: "⚖️",
      ch9: "🎌", ch10: "💼", ch11: "🗺️"
    };
    return icons[chapterId] || "📖";
  };

  const correctCount = Object.values(answers).filter(a => a.correct).length;
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  if (loading && !selectedChapter) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Free user view - show Chapter 1 & 2 as free, lock others
  if (isFreeUser && !selectedChapter) {
    return (
      <DashboardLayout auth={auth}>
        <div className="space-y-8" data-testid="chapter-practice-free">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Chapter Practice</h1>
            <p className="text-slate-600">Master each chapter with targeted practice questions</p>
          </div>

          {/* Free Trial Banner */}
          <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Free Chapter Practice Access
                  </h2>
                  <p className="text-slate-600 mb-2">
                    Try Chapters 1 & 2 for free! Experience our question format and detailed explanations.
                    Upgrade to unlock all 11 chapters with 500+ questions.
                  </p>
                </div>
                <div className="text-center">
                  <Link to="/pricing">
                    <Button className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white font-semibold h-12 px-8">
                      <Crown className="w-5 h-5 mr-2" />
                      Unlock All
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-maple-50 to-truenorth-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-maple rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {Object.values(chapterQuestionCounts).reduce((a, b) => a + b, 0)} Questions Total
                  </p>
                  <p className="text-slate-600">2 chapters free • {chapters.length - 2} chapters locked</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chapter Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chapters.map((chapter, index) => {
              const chapterId = chapter.id || chapter.chapter_id;
              const chapterKey = chapterId?.replace("chapter_", "ch") || `ch${index + 1}`;
              const questionCount = chapterQuestionCounts[chapterKey] || chapterQuestionCounts[chapterId] || 0;
              const isFree = isChapterFree(chapterId);
              
              return (
                <Card 
                  key={chapterId}
                  className={`border transition-all ${
                    isFree 
                      ? 'border-green-300 hover:border-green-400 hover:shadow-md cursor-pointer group bg-green-50/50' 
                      : 'border-slate-200 bg-slate-50/50 opacity-75'
                  }`}
                  onClick={() => isFree && startChapterPractice(chapterId)}
                  data-testid={`chapter-tile-${chapterId}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        isFree ? 'bg-green-100' : 'bg-slate-200'
                      }`}>
                        {isFree ? getChapterIcon(chapterKey) : <Lock className="w-6 h-6 text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Chapter {index + 1}</span>
                          {isFree ? (
                            <Badge className="bg-green-500 text-white text-xs">FREE</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-slate-200">
                              <Lock className="w-3 h-3 mr-1" /> Pro
                            </Badge>
                          )}
                        </div>
                        <h3 className={`font-heading font-semibold truncate ${isFree ? 'text-slate-900' : 'text-slate-500'}`}>
                          {chapter.title}
                        </h3>
                        <p className={`text-sm mt-1 ${isFree ? 'text-slate-600' : 'text-slate-400'}`}>
                          {questionCount} questions
                        </p>
                      </div>
                    </div>
                    {isFree && (
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-sm text-green-600 font-medium group-hover:underline">Start Practice</span>
                        <ChevronRight className="w-5 h-5 text-green-400 group-hover:text-green-600 transition-colors" />
                      </div>
                    )}
                    {!isFree && (
                      <div className="mt-4">
                        <Link to="/pricing" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" className="w-full text-sm">
                            Unlock Chapter
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Chapter Practice Mode (same as before for paid users)
  if (selectedChapter && questions.length > 0) {
    const question = questions[currentIndex];
    const chapterTitle = chapters.find(c => (c.id || c.chapter_id) === selectedChapter)?.title || "";

    return (
      <DashboardLayout auth={auth}>
        <div className="max-w-4xl mx-auto space-y-6" data-testid="chapter-practice">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={backToChapters} className="text-slate-600">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Chapters
            </Button>
            <div className="text-center">
              <h2 className="font-heading font-bold text-lg text-slate-900">{chapterTitle}</h2>
              <p className="text-sm text-slate-500">Question {currentIndex + 1} of {questions.length}</p>
            </div>
            <Button variant="outline" size="sm" onClick={resetPractice}>
              <RotateCw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Progress: {answeredCount}/{questions.length}</span>
              <span className="text-green-600 font-medium">{correctCount} correct</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-xs">{question.topic || "General"}</Badge>
                <Badge className={
                  question.difficulty === "easy" ? "bg-green-100 text-green-700" :
                  question.difficulty === "hard" ? "bg-red-100 text-red-700" :
                  "bg-yellow-100 text-yellow-700"
                }>
                  {question.difficulty || "medium"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-lg font-medium text-slate-900 mb-6">{question.question_text}</p>
              
              <div className="space-y-3">
                {Object.entries(question.options || {}).map(([key, value]) => {
                  const isSelected = selectedAnswer === key;
                  const isCorrect = key === question.correct_answer;
                  
                  let buttonClass = "w-full text-left p-4 rounded-lg border-2 transition-all ";
                  
                  if (showExplanation) {
                    if (isCorrect) {
                      buttonClass += "border-green-500 bg-green-50 text-green-900";
                    } else if (isSelected && !isCorrect) {
                      buttonClass += "border-red-500 bg-red-50 text-red-900";
                    } else {
                      buttonClass += "border-slate-200 bg-slate-50 text-slate-500";
                    }
                  } else {
                    buttonClass += isSelected 
                      ? "border-maple bg-maple-50 text-slate-900"
                      : "border-slate-200 hover:border-maple/50 hover:bg-slate-50";
                  }
                  
                  return (
                    <button key={key} className={buttonClass} onClick={() => handleAnswer(key)} disabled={showExplanation}>
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-white border flex items-center justify-center font-medium text-sm">{key}</span>
                        <span className="flex-1">{value}</span>
                        {showExplanation && isCorrect && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        {showExplanation && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {showExplanation && question.explanation && (
                <div className="mt-6 p-4 bg-truenorth-50 rounded-lg border border-truenorth-100">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-5 h-5 text-truenorth mt-0.5" />
                    <div>
                      <p className="font-medium text-truenorth mb-1">Explanation</p>
                      <p className="text-slate-700">{question.explanation}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={prevQuestion} disabled={currentIndex === 0}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <Button onClick={nextQuestion} disabled={currentIndex === questions.length - 1} className="bg-maple hover:bg-maple-600">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Chapter Selection View (for paid users)
  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="chapter-practice-home">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Chapter Practice</h1>
          <p className="text-slate-600">Master each chapter with targeted practice questions. Your progress is saved automatically.</p>
        </div>

        <Card className="border-0 shadow-sm bg-gradient-to-r from-maple-50 to-truenorth-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-maple rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {Object.values(chapterQuestionCounts).reduce((a, b) => a + b, 0)} Questions
                  </p>
                  <p className="text-slate-600">Across {chapters.length} chapters</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Your overall progress:</p>
                <p className="text-lg font-bold text-green-600">
                  {Object.values(chapterProgress).reduce((sum, p) => sum + (p.total_correct || 0), 0)} correct answers
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chapters.map((chapter, index) => {
            const chapterId = chapter.id || chapter.chapter_id;
            const chapterKey = chapterId?.replace("chapter_", "ch") || `ch${index + 1}`;
            const questionCount = chapterQuestionCounts[chapterKey] || chapterQuestionCounts[chapterId] || 0;
            const progress = chapterProgress[chapterId] || {};
            const attempted = progress.total_attempted || 0;
            const correct = progress.total_correct || 0;
            const wrongCount = progress.wrong_answers?.length || 0;
            const progressPercent = questionCount > 0 ? Math.round((attempted / questionCount) * 100) : 0;
            
            return (
              <Card 
                key={chapterId}
                className="border border-slate-200 hover:border-maple/50 hover:shadow-md transition-all cursor-pointer group"
                data-testid={`chapter-tile-${chapterId}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center text-2xl group-hover:bg-maple-50 transition-colors">
                      {getChapterIcon(chapterKey)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">Chapter {index + 1}</span>
                        {attempted > 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {correct}/{attempted} correct
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-heading font-semibold text-slate-900 truncate">{chapter.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{questionCount} questions</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  {attempted > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>{progressPercent}% completed</span>
                        <span>{attempted}/{questionCount}</span>
                      </div>
                      <Progress value={progressPercent} className="h-2" />
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center gap-2">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-maple hover:bg-maple-600"
                      onClick={() => startChapterPractice(chapterId)}
                    >
                      {attempted > 0 && attempted < questionCount ? "Continue" : "Start Practice"}
                    </Button>
                    {wrongCount > 0 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => startChapterPractice(chapterId, true)}
                      >
                        <RotateCw className="w-4 h-4 mr-1" />
                        Review {wrongCount}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChapterPractice;
